import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, ShieldAlert, X } from "lucide-react";

/**
 * In-app account closure — App Store Guideline 5.1.1(v). Prominent, reachable
 * in two taps from Company Settings, and honest about what is kept and why.
 */
export default function AccountClosure() {
  const [st, setSt] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/account/closure"); setSt(data); }
    catch { setSt(null); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/account/closure", {
        confirm_name: name, password: pw, reason,
      });
      toast.success(`Account closed. App data is deleted on ${String(data.purge_after).slice(0, 10)}.`);
      setOpen(false);
      setTimeout(() => { window.location.href = "/login"; }, 1800);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post("/account/closure/cancel");
      toast.success("Closure cancelled — your account is active again");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const p = st?.policy;

  if (st?.pending) {
    return (
      <div className="hard-card p-6 mt-8 border-bad bg-bad/5" data-testid="account-closure-pending">
        <div className="overline mb-2 flex items-center gap-2 text-bad">
          <ShieldAlert className="w-4 h-4" /> § ACCOUNT SCHEDULED FOR CLOSURE
        </div>
        <p className="font-mono text-xs mb-1">
          Requested {String(st.closure_requested_at).slice(0, 10)} by {st.requested_by}. Every other
          login for <span className="font-black">{st.company_name}</span> is already switched off.
        </p>
        <p className="font-mono text-xs text-muted-foreground mb-4 max-w-2xl">
          App data is permanently deleted on <span className="font-black" data-testid="closure-purge-date">{String(st.purge_after).slice(0, 10)}</span>.
          Payroll and tax records stay sealed until {String(st.retain_until).slice(0, 10)}, then they
          go too. Cancel before the purge date and everything comes straight back.
        </p>
        <button data-testid="closure-cancel-btn" disabled={busy} onClick={cancel}
          className="hard-btn px-5 py-3 font-mono uppercase text-xs tracking-widest">
          {busy ? "Working…" : "Cancel closure — keep my account"}
        </button>
      </div>
    );
  }

  return (
    <div className="hard-card p-6 mt-8 border-bad bg-bad/5" data-testid="account-closure-section">
      <div className="overline mb-2 flex items-center gap-2 text-bad">
        <Trash2 className="w-4 h-4" /> § DELETE MY ACCOUNT
      </div>
      <p className="font-mono text-xs text-muted-foreground mb-4 max-w-2xl">
        Closes <span className="font-black">{st?.company_name || "this company"}</span> and deletes it
        from Checkmate — no phone call, no support ticket. Every login goes off immediately. You have{" "}
        {p?.grace_days ?? 30} days to change your mind, then employees, timesheets, documents and all
        app data are permanently deleted. Payroll and tax records are sealed and kept for{" "}
        {p?.retain_years ?? 4} years because the IRS requires it, then deleted automatically.
      </p>
      {st?.counts && (
        <div className="font-mono text-[11px] mb-4 flex flex-wrap gap-x-6 gap-y-1" data-testid="closure-counts">
          <span>{st.counts.employees} employee record(s)</span>
          <span>{st.counts.logins} login(s)</span>
          <span>{st.counts.pay_stubs} pay stub(s) sealed</span>
          <span>{st.counts.payroll_runs} payroll run(s) sealed</span>
        </div>
      )}
      <button data-testid="account-delete-btn" onClick={() => setOpen(true)}
        className="border-2 border-bad bg-bad text-white px-5 py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 hover:-translate-x-0.5 hover:-translate-y-0.5 transition-transform">
        <Trash2 className="w-4 h-4" /> Delete My Account
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-4" data-testid="closure-modal">
          <div className="bg-white border-2 border-ink shadow-hard w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div className="overline text-bad">§ CONFIRM ACCOUNT DELETION</div>
              <button data-testid="closure-modal-close" onClick={() => setOpen(false)} className="p-1"><X className="w-4 h-4" /></button>
            </div>
            <ul className="font-mono text-xs space-y-2 mb-5 list-disc pl-5">
              <li>All logins for this company stop working the moment you confirm.</li>
              <li>Employees, timesheets, time-off, documents and worksites are permanently
                  deleted after {p?.grace_days ?? 30} days.</li>
              <li>Pay stubs, payroll runs, filings and the audit log are sealed for{" "}
                  {p?.retain_years ?? 4} years — {p?.retained_reason}</li>
              <li>Cancel any time inside the {p?.grace_days ?? 30} days by signing back in with this
                  same admin login.</li>
            </ul>
            <label className="block mb-3">
              <div className="overline mb-1">Type the company name to confirm</div>
              <input data-testid="closure-confirm-name" value={name} onChange={(e) => setName(e.target.value)}
                placeholder={st?.company_name || ""} className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" />
            </label>
            <label className="block mb-3">
              <div className="overline mb-1">Your password</div>
              <input data-testid="closure-confirm-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" />
            </label>
            <label className="block mb-5">
              <div className="overline mb-1">Why are you leaving? (optional)</div>
              <input data-testid="closure-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" />
            </label>
            <div className="flex flex-wrap gap-3">
              <button data-testid="closure-confirm-btn" disabled={busy || !name || !pw} onClick={submit}
                className="border-2 border-bad bg-bad text-white px-5 py-3 font-mono uppercase text-xs tracking-widest disabled:opacity-40">
                {busy ? "Deleting…" : "Delete my account"}
              </button>
              <button data-testid="closure-keep-btn" onClick={() => setOpen(false)}
                className="border-2 border-ink px-5 py-3 font-mono uppercase text-xs tracking-widest hover:bg-secondary">
                Keep my account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
